/**
 * SoftphoneEmbed — launches Telzio's official browser softphone in a popup
 * window (iframe blocked by Telzio's auth flow X-Frame-Options).
 *
 * UX: Single button opens a sized, dockable popup window (700×900) that
 * persists across page navigations in Ventas OS. The popup is auto-restored
 * if it gets closed accidentally.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, ExternalLink, AlertCircle, Maximize2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const TELZIO_URL = 'https://telzio.com/dashboard';
const POPUP_W = 480;
const POPUP_H = 820;

const SoftphoneEmbed = () => {
  const popupRef = useRef(null);
  const [popupOpen, setPopupOpen] = useState(false);

  const openPopup = useCallback(() => {
    // If already open, focus it
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    const w = POPUP_W, h = POPUP_H;
    const left = Math.max(0, window.screenX + (window.outerWidth - w));
    const top = window.screenY + 60;
    // `popup=yes` (Chrome 96+) is required to force a true popup window
    // instead of a new tab. Order: popup first, then dimensions.
    const features = `popup=yes,width=${w},height=${h},left=${left},top=${top}`;
    const popup = window.open(TELZIO_URL, 'TelzioSoftphone', features);
    popupRef.current = popup;
    setPopupOpen(!!popup);
    if (!popup) {
      // Popup blocked
      alert('Popup was blocked. Please allow popups for this site or use "Open in new tab".');
    }
  }, []);

  // Watch for popup close so the button label updates
  useEffect(() => {
    if (!popupOpen) return;
    const tick = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        setPopupOpen(false);
        popupRef.current = null;
        clearInterval(tick);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [popupOpen]);

  return (
    <div className="space-y-5 max-w-3xl mx-auto p-6" data-testid="softphone-embed-page">
      <div className="flex items-center gap-2">
        <Phone size={22} className="text-brand" />
        <h1 className="text-2xl font-bold text-text-primary">Browser Softphone</h1>
      </div>

      {/* Hero card */}
      <Card>
        <CardContent className="p-8 text-center space-y-5">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-brand/10 flex items-center justify-center">
            <Phone size={36} className="text-brand" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary mb-2">Open the Telzio Web Phone</h2>
            <p className="text-sm text-text-secondary max-w-md mx-auto">
              Launches Telzio in a sized popup window so you can dock it beside Ventas OS. Sign in once — Telzio remembers the session across pages.
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={openPopup}
              size="lg"
              className="bg-brand hover:bg-brand-hover"
              data-testid="softphone-embed-open-popup"
            >
              <Maximize2 size={16} className="mr-2" />
              {popupOpen ? 'Focus Telzio popup' : 'Open Telzio in popup'}
            </Button>
            <a
              href={TELZIO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="softphone-embed-open-tab"
            >
              <Button variant="outline" size="lg">
                <ExternalLink size={16} className="mr-2" /> New tab
              </Button>
            </a>
          </div>
          {popupOpen && (
            <p className="text-xs text-emerald-700 font-medium" data-testid="softphone-embed-status">
              ✓ Telzio popup is open
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notice */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-3 flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-amber-900">
            <p className="font-medium mb-1">Why a popup instead of embedded?</p>
            <p className="leading-relaxed">
              Telzio blocks iframe login for security (standard SaaS practice). The fully-integrated in-browser softphone — where calls happen directly inside Ventas OS — needs a WebSocket path and SIP token from Telzio that aren't in their public REST API. The JsSIP scaffold is ready in <code className="bg-amber-100 px-1 rounded">useTelzioSoftphone.js</code>; once Telzio support shares those, we'll flip the switch.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SoftphoneEmbed;

